import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { X, ShieldCheck, Undo2, ArrowRightCircle, Save, History } from 'lucide-react';
import { toast } from './notify';
import { StageBadge, STAGES, DeptRoute, deptLabel } from './VisitBits';
import EditorItems, { AddItemBox, KINDS } from './EditorItems';
import { formatCATDateTime } from '../../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOTE_FIELDS = [
  ['complaints', 'Patient complaints'],
  ['provisional_diagnosis', 'Provisional/Differential diagnosis'],
  ['final_diagnosis', 'Final diagnosis'],
  ['notes', 'Physical examination'],
];
const QUICK_STAGES = [
  { status: 'RETURN_TO_DOCTOR', label: 'Return to Doctor', cls: 'bg-indigo-600 hover:bg-indigo-700' },
  { status: 'LAB_PENDING', label: 'Return to Lab', cls: 'bg-purple-600 hover:bg-purple-700' },
  { status: 'RAD_PENDING', label: 'Return to Radiology', cls: 'bg-sky-600 hover:bg-sky-700' },
  { status: 'DENTAL_PENDING', label: 'Return to Dental', cls: 'bg-teal-600 hover:bg-teal-700' },
  { status: 'PHARMACY_PENDING', label: 'Send to Pharmacy', cls: 'bg-cyan-600 hover:bg-cyan-700' },
  { status: 'READY_FOR_BILLING', label: 'Send to Billing', cls: 'bg-green-600 hover:bg-green-700' },
];

// Superadmin-only full visit editor: items, results, notes and stage — every change is audited.
export default function VisitEditor({ visitId, authCfg, onClose, refresh }) {
  const [visit, setVisit] = useState(null);
  const [priceList, setPriceList] = useState([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState({});
  const [stage, setStage] = useState('');
  const [busy, setBusy] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/visits/${visitId}`, authCfg);
      setVisit(res.data);
      const c = res.data.consultation || {};
      setNotes(Object.fromEntries(NOTE_FIELDS.map(([k]) => [k, c[k] || ''])));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load visit');
    }
  }, [visitId, authCfg]);

  useEffect(() => {
    load();
    axios.get(`${API}/admin/visits/${visitId}/pricelist`, authCfg).then((r) => setPriceList(r.data.items || [])).catch(() => {});
  }, [load, visitId, authCfg]);

  const changed = () => { load(); refresh(); };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.data?.unchanged) { toast.info('No changes to save'); return; }
      toast.success(okMsg + (res?.data?.voided_claim_id ? ` — claim ${res.data.voided_claim_id} voided, family balance restored` : ''));
      changed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = () => act(() => axios.put(`${API}/admin/visits/${visitId}/notes`, { ...notes, reason }, authCfg), 'Doctor notes updated');
  const moveStage = (status) => act(() => axios.post(`${API}/admin/visits/${visitId}/stage`, { status, reason }, authCfg), `Visit moved to ${status === 'RETURN_TO_DOCTOR' ? 'the doctor' : (STAGES[status]?.label || status)}`);

  if (!visit) return <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">Loading visit…</div>;

  const pendingTests = KINDS.filter((k) => k.hasResult).flatMap((k) => (visit[k.field] || []).filter((o) => o.status === 'pending').map((o) => ({ ...o, stage: k.stage })));
  const stageMismatch = pendingTests.length > 0 && !['LAB_PENDING', 'RAD_PENDING', 'DENTAL_PENDING', 'DIAGNOSTICS_PENDING', 'AWAITING_LAB_APPROVAL', 'AWAITING_RAD_APPROVAL', 'AWAITING_DENTAL_APPROVAL', 'WAITING_CONSULTANT', 'IN_CONSULTATION'].includes(visit.status);
  const edits = visit.admin_edits || [];

  return (
    <Card className="border-amber-300 shadow-md" data-testid="visit-editor">
      <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 p-4">
        <CardTitle className="flex items-center justify-between text-amber-900 text-base gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Manage Visit — {visit.patient_name}</span>
            <StageBadge status={visit.status} />
          </span>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="visit-editor-close"><X className="w-4 h-4 mr-1" /> Close</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 space-y-1">
          <p>{visit.visit_id} • Serial: {visit.patient_serial_number} • {visit.hospital_name} • Checked in {formatCATDateTime(visit.checked_in_at)} by {visit.checked_in_by}</p>
          <DeptRoute visit={visit} />
          {visit.billing?.claim_id && <p className="text-emerald-700 font-medium" data-testid="editor-billing-note">Billed — claim {visit.billing.claim_id} ({visit.billing.currency_code} {Number(visit.billing.total_amount_local).toFixed(2)}). Item changes update the claim; moving the stage back voids it and restores the family balance.</p>}
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600">Reason for change (optional — stamped on every edit below)</label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Test entered on wrong patient" className="mt-1 bg-white" data-testid="editor-reason" />
        </div>

        <div className="border border-indigo-200 rounded-xl p-3 bg-indigo-50/40" data-testid="editor-stage">
          <p className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1"><ArrowRightCircle className="w-4 h-4 text-indigo-600" /> Move patient</p>
          {stageMismatch && (
            <p className="text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded p-2 mb-2" data-testid="editor-stage-warning">
              {pendingTests.length} pending test(s) ({pendingTests.map((t) => t.item_name).join(', ')}) but the visit is not in a test queue — send it to {deptLabelForStage(pendingTests[0].stage)} so they get done.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {QUICK_STAGES.map((s) => (
              <Button key={s.status} size="sm" disabled={busy || s.status === visit.status} onClick={() => moveStage(s.status)} className={`${s.cls} h-8 text-xs`} data-testid={`editor-stage-${s.status}`}>
                {s.status === 'RETURN_TO_DOCTOR' && <Undo2 className="w-3.5 h-3.5 mr-1" />}{s.label}
              </Button>
            ))}
          </div>
          <div className="flex gap-2 mt-2 items-center">
            <select value={stage} onChange={(e) => setStage(e.target.value)} className="h-8 text-xs border border-gray-300 rounded-md px-2 bg-white" data-testid="editor-stage-select">
              <option value="">Any other stage…</option>
              {Object.entries(STAGES).filter(([k]) => !['BILLED', 'CANCELLED'].includes(k)).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <Button size="sm" variant="outline" disabled={!stage || busy || stage === visit.status} onClick={() => moveStage(stage)} className="h-8 text-xs" data-testid="editor-stage-apply">Apply</Button>
          </div>
        </div>

        <div className="border border-blue-200 rounded-xl p-3 bg-blue-50/30" data-testid="editor-notes">
          <p className="text-sm font-bold text-gray-800 mb-2">Doctor notes {visit.consultation?.consultant ? <span className="text-gray-400 font-normal">— {visit.consultation.consultant} ({deptLabel(visit.consultation.department || visit.current_department)})</span> : null}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {NOTE_FIELDS.map(([k, label]) => (
              <div key={k}>
                <label className="text-xs text-gray-600">{label}</label>
                <textarea value={notes[k] || ''} onChange={(e) => setNotes((p) => ({ ...p, [k]: e.target.value }))} rows={2} className="mt-0.5 w-full rounded-md border border-gray-300 p-2 text-sm bg-white focus:border-teal-500 focus:outline-none" data-testid={`editor-note-${k}`} />
              </div>
            ))}
          </div>
          <Button size="sm" onClick={saveNotes} disabled={busy} className="mt-2 bg-blue-600 hover:bg-blue-700 h-8 text-xs" data-testid="editor-notes-save"><Save className="w-3.5 h-3.5 mr-1" /> Save notes</Button>
        </div>

        <AddItemBox visit={visit} authCfg={authCfg} priceList={priceList} reason={reason} onChanged={changed} />
        {KINDS.map((def) => <EditorItems key={def.kind} visit={visit} def={def} authCfg={authCfg} reason={reason} onChanged={changed} />)}

        {edits.length > 0 && (
          <div className="border border-gray-200 rounded-xl" data-testid="editor-audit">
            <button onClick={() => setShowLog((s) => !s)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-bold text-gray-800" data-testid="editor-audit-toggle">
              <span className="flex items-center gap-1"><History className="w-4 h-4 text-gray-500" /> Acacia office edit history ({edits.length})</span>
              <span className="text-xs text-gray-400">{showLog ? 'Hide' : 'Show'}</span>
            </button>
            {showLog && (
              <ul className="border-t border-gray-100 divide-y text-xs text-gray-700">
                {[...edits].reverse().map((e, i) => (
                  <li key={i} className="px-3 py-1.5"><span className="text-gray-400">{formatCATDateTime(e.at)}</span> • <span className="font-medium">{e.by}</span> — {e.detail}{e.reason ? <span className="text-amber-700"> ({e.reason})</span> : null}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const deptLabelForStage = (s) => ({ LAB_PENDING: 'the Laboratory', RAD_PENDING: 'Radiology', DENTAL_PENDING: 'Dental' }[s] || 'the department');
